import React from 'react';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/UI/Typography';
import classNames from 'classnames';
import CardContent from '@material-ui/core/CardContent';
import Card from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Card';
import PropTypes from 'prop-types';
import { CardActionArea } from '@material-ui/core';
import Skeleton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Skeleton';
import { noop } from 'Helpers/PropTypes';
import MuiCloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  card: {
    borderRadius: '10px',
    width: '300px',
  },
  primary: {
    borderLeft: `solid ${theme.palette.primary.main} 8px`,
  },
  success: {
    borderLeft: `solid ${theme.palette.success} 8px`,
  },
  warning: {
    borderLeft: `solid ${theme.palette.warning} 8px`,
  },
  danger: {
    borderLeft: `solid ${theme.palette.error} 8px`,
  },
  cardContainer: {
    display: 'flex',
    width: '100%',
  },
  content: {
    paddingRight: theme.spacing(8),
  },
  closeButton: {
    width: 'auto',
    marginLeft: 'auto',
    padding: theme.spacing(0, 1),
    color: theme.palette.facet.primaryBlue,
  },
  subtitle: {
    marginTop: theme.spacing(2),
  },
  body: {
    marginTop: theme.spacing(1),
  },

}));

export const MessageCardSkeleton = ({
  className,
}) => {
  const classes = useStyles();
  return (
    <Card
      className={classNames(
        classes.card,
        className,
      )}
    >
      <CardContent className={classes.content}>
        <Typography
          variant="h6"
        >
          <Skeleton width="100%" />
        </Typography>
        <Typography
          className={classes.subtitle}
          variant="h6"
        >
          <Skeleton width="100%" />
        </Typography>
      </CardContent>
    </Card>
  );
};

MessageCardSkeleton.defaultProps = {
  className: '',
};

MessageCardSkeleton.propTypes = {
  className: PropTypes.string,
};

const MessageCard = ({
  className,
  title,
  sendAt,
  onClick,
  closeButton,
  onClose,
  children,
  variant,
  subject,
  content,
  ...rest
}) => {
  const classes = useStyles();

  const Content = () => (
    <CardContent className={classes.content}>
      <Typography
        color={'primary'}
        variant={'h6'}
      >
        {title}
      </Typography>
      <Typography
        className={classes.subtitle}
        variant={'body1'}
        weight={'bold'}
        color={'primaryGray'}
      >
        {subject}
      </Typography>
      <Typography
        className={classes.body}
        variant={'body1'}
        color={'primaryGray'}
      >
        {content}
      </Typography>
      {children}
    </CardContent>
  );

  return (
    <Card
      className={classNames(
        classes.root,
        classes.card,
        {
          [classes.primary]: variant === 'primary',
          [classes.success]: variant === 'success',
          [classes.error]: variant === 'error',
          [classes.warning]: variant === 'warning',
        },
        className,
      )}
      elevation={3}
      {...rest}
    >
      {onClick ? (
        <div className={classes.cardContainer}>
          <CardActionArea onClick={onClick}>
            <Content />
          </CardActionArea>
          {closeButton && (
            <CardActionArea
              className={classes.closeButton}
              color="primary"
              onClick={onClose}
              aria-label="close"
              test={'recommended-message-card-close'}
            >
              <MuiCloseIcon fontSize={'small'} />
            </CardActionArea>
          )}
        </div>
      ) : (
        <div className={classes.cardContainer}>
          <Content />
          {closeButton && (
            <CardActionArea
              className={classes.closeButton}
              onClick={onClose}
              aria-label="close"
              test={'recommended-message-card-close'}
            >
              <MuiCloseIcon fontSize={'small'} />
            </CardActionArea>
          )}
        </div>
      )}
    </Card>
  );
};

MessageCard.defaultProps = {
  className: '',
  onClick: noop,
  onClose: noop,
  children: null,
  closeButton: false,
  variant: 'primary',
  subject: '',
  content: '',
};

MessageCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  sendAt: PropTypes.string.isRequired,
  closeButton: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  children: PropTypes.node,
  variant: PropTypes.string,
  subject: PropTypes.string,
  content: PropTypes.string,
};

export default MessageCard;
